import * as React from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '@/utils/utils.ts';
import { cva, VariantProps } from 'class-variance-authority';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva(
  'aspect-square h-4 w-4 rounded-full border shadow-sm focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        green: 'border-green-600/20 text-green-700 data-[state=checked]:bg-green-50',
        yellow: 'border-yellow-600/20 text-yellow-800 data-[state=checked]:bg-yellow-50',
        red: 'border-red-600/10 text-red-700 data-[state=checked]:bg-red-50',
        gray: 'border-gray-500/10 text-gray-600 data-[state=checked]:bg-gray-50',
        purple: 'border-purple-700/10 text-purple-700 data-[state=checked]:bg-purple-50',
        blue: 'border-blue-700/10 text-blue-700 data-[state=checked]:bg-blue-50',
        indigo: 'border-indigo-700/10 text-indigo-700 data-[state=checked]:bg-indigo-50',
        pink: 'border-pink-700/10 text-pink-700 data-[state=checked]:bg-pink-50',
      },
    },
    defaultVariants: {
      variant: 'green',
    },
  }
);

interface RadioGroupItemProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ variant }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <CheckIcon className="h-3.5 w-3.5" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
